import React, { useEffect, useState } from 'react';
import { getSettings, updateSettings } from '../../../api/eliteGameApi';

// ToggleSwitch: label on top, switch below, treated as a field
const ToggleSwitch = ({ checked, onChange, label }) => (
  <div className="flex flex-col mb-2 min-w-[180px]">
    <label className="font-semibold mb-1 flex items-center">{label}</label>
    <span
      className={`relative inline-block w-10 h-6 rounded-full cursor-pointer transition-colors ${
        checked ? 'bg-[#00C09A]' : 'bg-gray-300'
      }`}
      onClick={() => onChange(!checked)}
      role="switch"
      tabIndex={0}
    >
      <span
        className={`absolute left-1 top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
          checked ? 'translate-x-4' : ''
        }`}
      />
    </span>
  </div>
);

const InputField = ({ label, value, onChange, type = 'text', required = false, children }) => (
  <div className="flex flex-col mb-2 min-w-[180px]">
    <label className="font-semibold mb-1 flex items-center">
      {label}
      {required && <span className="text-red-600 font-bold ml-1 text-base">*</span>}
    </label>
    {children ? (
      children
    ) : (
      <input
        className="px-2 py-2 rounded-md border border-gray-400 bg-white text-base focus:outline-none focus:ring-2 focus:ring-[#00C09A]"
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
      />
    )}
  </div>
);

const SelectField = ({ label, value, options, onChange, required = false }) => (
  <InputField label={label} required={required}>
    <select
      className="w-full px-2 py-2 rounded-md border border-gray-400 bg-white text-base focus:outline-none focus:ring-2 focus:ring-[#00C09A]"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
    >
      {options.map((opt) => (
        <option value={opt.value} key={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  </InputField>
);

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="font-bold text-2xl mb-3 flex items-center gap-2">
      {title} <span className="text-red-600 text-2xl font-bold">?</span>
    </h2>
    <div className="bg-[#F1F1F1] rounded-xl p-6">{children}</div>
  </section>
);

const wrapRowClass = 'flex flex-wrap gap-6 mb-2 items-end';

const defaultSettings = {
  maximum_winning_coins: '4',
  minimum_coins_winning_percentage: '70',
  quiz_winning_percentage: '30',
  score: '4',
  answer_mode: '1',
  review_answers_deduct_coin: '10',
  welcome_bonus: '500',
  question_shuffle: 1,
  option_shuffle: 1,

  quiz_zone_mode: 1,
  quiz_zone_fix_question: 1,
  quiz_zone_total_questions: '10',
  quiz_zone_duration: '30',
  quiz_zone_lifeline_deduct_coin: '5',
  quiz_zone_wrong_answer_deduct_score: '4',
  quiz_zone_correct_answer_credit_score: '4',

  guess_the_word_question: 1,
  guess_the_word_fix_question: 1,
  guess_the_word_total_questions: '10',
  guess_the_word_seconds: '30',
  guess_the_word_max_hints: '2',
  guess_the_word_max_winning_coin: '5',
  guess_the_word_wrong_answer_deduct_score: '4',
  guess_the_word_correct_answer_credit_score: '4',
  guess_the_word_hint_deduct_coin: '1',

  audio_mode_question: 1,
  audio_quiz_fix_question: 1,
  audio_quiz_total_questions: '10',
  audio_quiz_total_time: '300',
  audio_quiz_seconds: '30',
  audio_quiz_wrong_answer_deduct_score: '4',
  audio_quiz_correct_answer_credit_score: '4',

  maths_quiz_mode: 1,
  maths_quiz_fix_question: 1,
  maths_quiz_total_questions: '10',
  maths_quiz_seconds: '30',
  maths_quiz_wrong_answer_deduct_score: '4',
  maths_quiz_correct_answer_credit_score: '4',

  fun_n_learn_question: 1,
  fun_n_learn_fix_question: 1,
  fun_n_learn_total_questions: '10',
  fun_and_learn_time_in_seconds: '30',
  fun_n_learn_quiz_wrong_answer_deduct_score: '4',
  fun_n_learn_quiz_correct_answer_credit_score: '4',

  exam_module: 1,
  exam_module_resume_exam_timeout: '5',

  contest_mode: 1,
  contest_mode_wrong_deduct_score: '4',
  contest_mode_correct_credit_score: '4',

  multi_match_mode: 1,
  multi_match_fix_level_question: 1,
  multi_match_total_level_question: '10',
  multi_match_duration: '30',
  multi_match_wrong_answer_deduct_score: '10',
  multi_match_correct_answer_credit_score: '20'
};

const QuizSettingsPage = () => {
  const [settings, setSettings] = useState(defaultSettings);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await getSettings();
        console.log('Fetched settings:', res.data);
        setSettings({ ...defaultSettings, ...res.data }); // fallback for missing keys
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchData();
  }, []);

  const handleChange = (key, value) => {
    setSettings((prev) => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await updateSettings(settings); // implement PATCH/PUT here
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <form onSubmit={handleSubmit} className=" mx-auto py-8 px-2 sm:px-6 lg:px-12 bg-[#fafafa]">
      {/* General Settings */}
      <Section title="General Settings">
        <div className={wrapRowClass}>
          <InputField
            label="Maximum Winning Coins"
            value={settings.maximum_winning_coins}
            onChange={(v) => handleChange('maximum_winning_coins', v)}
            required
          />
          <InputField
            label="Minimum Winning Percentage"
            value={settings.minimum_coins_winning_percentage}
            onChange={(v) => handleChange('minimum_coins_winning_percentage', v)}
            required
          />
          <InputField
            label="Quiz Winning Percentage"
            value={settings.quiz_winning_percentage}
            onChange={(v) => handleChange('quiz_winning_percentage', v)}
            required
          />
          <InputField
            label="Score"
            value={settings.score}
            onChange={(v) => handleChange('score', v)}
            required
          />
          <SelectField
            label="Answer Mode"
            value={settings.answer_mode}
            options={[
              { value: '1', label: 'Show Answer Correctness' },
              { value: '2', label: 'Hide Answer Correctness' }
            ]}
            onChange={(v) => handleChange('answer_mode', v)}
            required
          />
          <InputField
            label="Review Answers Deduct"
            value={settings.review_answers_deduct_coin}
            onChange={(v) => handleChange('review_answers_deduct_coin', v)}
            required
          />
          <ToggleSwitch
            label="Option Shuffle"
            checked={!!settings.option_shuffle}
            onChange={(v) => handleChange('option_shuffle', v)}
          />
          <ToggleSwitch
            label="Question Shuffle"
            checked={!!settings.question_shuffle}
            onChange={(v) => handleChange('question_shuffle', v)}
          />
        </div>
        <div className="mb-2">
          <InputField
            label="Welcome Bonus"
            value={settings.welcome_bonus}
            onChange={(v) => handleChange('welcome_bonus', v)}
            required
          />
        </div>
      </Section>

      {/* Quiz Zone Settings */}
      <Section title="Quiz Zone Settings">
        <div className={wrapRowClass}>
          <ToggleSwitch
            label="Visible Mode"
            checked={!!settings.quiz_zone_mode}
            onChange={(v) => handleChange('quiz_zone_mode', v)}
          />
          <ToggleSwitch
            label="Fix Question in Level"
            checked={!!settings.quiz_zone_fix_question}
            onChange={(v) => handleChange('quiz_zone_fix_question', v)}
          />
          <InputField
            label="Total Question per Level"
            value={settings.quiz_zone_total_questions}
            onChange={(v) => handleChange('quiz_zone_total_questions', v)}
          />
          <InputField
            label="Quiz Zone Duration (in seconds)"
            value={settings.quiz_zone_duration}
            onChange={(v) => handleChange('quiz_zone_duration', v)}
            required
          />
          <InputField
            label="Lifeline Deduct"
            value={settings.quiz_zone_lifeline_deduct_coin}
            onChange={(v) => handleChange('quiz_zone_lifeline_deduct_coin', v)}
            required
          />
          <InputField
            label="Wrong Answer Deduct Score"
            value={settings.quiz_zone_wrong_answer_deduct_score}
            onChange={(v) => handleChange('quiz_zone_wrong_answer_deduct_score', v)}
            required
          />
        </div>
        <div className="mb-2">
          <InputField
            label="Correct Answer Credit Score"
            value={settings.quiz_zone_correct_answer_credit_score}
            onChange={(v) => handleChange('quiz_zone_correct_answer_credit_score', v)}
            required
          />
        </div>
      </Section>

      {/* Guess The Word Settings */}
      <Section title="Guess The Word Settings">
        <div className={wrapRowClass}>
          <ToggleSwitch
            label="Visible Mode"
            checked={!!settings.guess_the_word_question}
            onChange={(v) => handleChange('guess_the_word_question', v)}
          />
          <ToggleSwitch
            label="Fix Question"
            checked={!!settings.guess_the_word_fix_question}
            onChange={(v) => handleChange('guess_the_word_fix_question', v)}
          />
          <InputField
            label="Total Question Limit"
            value={settings.guess_the_word_total_questions}
            onChange={(v) => handleChange('guess_the_word_total_questions', v)}
          />
          <InputField
            label="Guess The Word Duration (in seconds)"
            value={settings.guess_the_word_seconds}
            onChange={(v) => handleChange('guess_the_word_seconds', v)}
            required
          />
          <InputField
            label="Max Hint Per Question"
            value={settings.guess_the_word_max_hints}
            onChange={(v) => handleChange('guess_the_word_max_hints', v)}
            required
          />
          <InputField
            label="Guess The Word Max Winning"
            value={settings.guess_the_word_max_winning_coin}
            onChange={(v) => handleChange('guess_the_word_max_winning_coin', v)}
            required
          />
          <InputField
            label="Wrong Answer Deduct Score"
            value={settings.guess_the_word_wrong_answer_deduct_score}
            onChange={(v) => handleChange('guess_the_word_wrong_answer_deduct_score', v)}
            required
          />
          <InputField
            label="Correct Answer Credit Score"
            value={settings.guess_the_word_correct_answer_credit_score}
            onChange={(v) => handleChange('guess_the_word_correct_answer_credit_score', v)}
            required
          />
          <InputField
            label="Hint Deduct Coins"
            value={settings.guess_the_word_hint_deduct_coin}
            onChange={(v) => handleChange('guess_the_word_hint_deduct_coin', v)}
            required
          />
        </div>
      </Section>

      {/* Audio Quiz Settings */}
      <Section title="Audio Quiz Settings">
        <div className={wrapRowClass}>
          <ToggleSwitch
            label="Visible Mode"
            checked={!!settings.audio_mode_question}
            onChange={(v) => handleChange('audio_mode_question', v)}
          />
          <ToggleSwitch
            label="Fix Question"
            checked={!!settings.audio_quiz_fix_question}
            onChange={(v) => handleChange('audio_quiz_fix_question', v)}
          />
          <InputField
            label="Total Question Limit"
            value={settings.audio_quiz_total_questions}
            onChange={(v) => handleChange('audio_quiz_total_questions', v)}
          />
          <InputField
            label="Total Audio Time Of File (in seconds)"
            value={settings.audio_quiz_total_time}
            onChange={(v) => handleChange('audio_quiz_total_time', v)}
            required
          />
          <InputField
            label="Audio Quiz Duration (in seconds)"
            value={settings.audio_quiz_seconds}
            onChange={(v) => handleChange('audio_quiz_seconds', v)}
            required
          />
          <InputField
            label="Wrong Answer Deduct Score"
            value={settings.audio_quiz_wrong_answer_deduct_score}
            onChange={(v) => handleChange('audio_quiz_wrong_answer_deduct_score', v)}
            required
          />
          <InputField
            label="Correct Answer Credit Score"
            value={settings.audio_quiz_correct_answer_credit_score}
            onChange={(v) => handleChange('audio_quiz_correct_answer_credit_score', v)}
            required
          />
        </div>
      </Section>

      {/* Maths Quiz Settings */}
      <Section title="Maths Quiz Settings">
        <div className={wrapRowClass}>
          <ToggleSwitch
            label="Visible Mode"
            checked={!!settings.maths_quiz_mode}
            onChange={(v) => handleChange('maths_quiz_mode', v)}
          />
          <ToggleSwitch
            label="Fix Question"
            checked={!!settings.maths_quiz_fix_question}
            onChange={(v) => handleChange('maths_quiz_fix_question', v)}
          />
          <InputField
            label="Total Question Limit"
            value={settings.maths_quiz_total_questions}
            onChange={(v) => handleChange('maths_quiz_total_questions', v)}
          />
          <InputField
            label="Maths Quiz Duration (in seconds)"
            value={settings.maths_quiz_seconds}
            onChange={(v) => handleChange('maths_quiz_seconds', v)}
            required
          />
          <InputField
            label="Wrong Answer Deduct Score"
            value={settings.maths_quiz_wrong_answer_deduct_score}
            onChange={(v) => handleChange('maths_quiz_wrong_answer_deduct_score', v)}
            required
          />
          <InputField
            label="Correct Answer Credit Score"
            value={settings.maths_quiz_correct_answer_credit_score}
            onChange={(v) => handleChange('maths_quiz_correct_answer_credit_score', v)}
            required
          />
        </div>
      </Section>

      {/* Fun N Learn Quiz Settings */}
      <Section title="Fun N Learn Quiz Settings">
        <div className={wrapRowClass}>
          <ToggleSwitch
            label="Visible Mode"
            checked={!!settings.fun_n_learn_question}
            onChange={(v) => handleChange('fun_n_learn_question', v)}
          />
          <ToggleSwitch
            label="Fix Question"
            checked={!!settings.fun_n_learn_fix_question}
            onChange={(v) => handleChange('fun_n_learn_fix_question', v)}
          />
          <InputField
            label="Total Question Limit"
            value={settings.fun_n_learn_total_questions}
            onChange={(v) => handleChange('fun_n_learn_total_questions', v)}
          />
          <InputField
            label="Fun N Learn Quiz Duration (in seconds)"
            value={settings.fun_and_learn_time_in_seconds}
            onChange={(v) => handleChange('fun_and_learn_time_in_seconds', v)}
            required
          />
          <InputField
            label="Wrong Answer Deduct Score"
            value={settings.fun_n_learn_quiz_wrong_answer_deduct_score}
            onChange={(v) => handleChange('fun_n_learn_quiz_wrong_answer_deduct_score', v)}
            required
          />
          <InputField
            label="Correct Answer Credit Score"
            value={settings.fun_n_learn_quiz_correct_answer_credit_score}
            onChange={(v) => handleChange('fun_n_learn_quiz_correct_answer_credit_score', v)}
            required
          />
        </div>
      </Section>

      {/* Exam Module Quiz Settings */}
      <Section title="Exam Module Quiz Settings">
        <div className={wrapRowClass}>
          <ToggleSwitch
            label="Visible Mode"
            checked={!!settings.exam_module}
            onChange={(v) => handleChange('exam_module', v)}
          />
          <InputField
            label="Resume Exam After Close Timeout (in seconds)"
            value={settings.exam_module_resume_exam_timeout}
            onChange={(v) => handleChange('exam_module_resume_exam_timeout', v)}
            required
          />
        </div>
      </Section>

      {/* Contest Quiz Settings */}
      <Section title="Contest Quiz Settings">
        <div className={wrapRowClass}>
          <ToggleSwitch
            label="Visible Mode"
            checked={!!settings.contest_mode}
            onChange={(v) => handleChange('contest_mode', v)}
          />
          <InputField
            label="Wrong Answer Deduct Score"
            value={settings.contest_mode_wrong_deduct_score}
            onChange={(v) => handleChange('contest_mode_wrong_deduct_score', v)}
            required
          />
          <InputField
            label="Correct Answer Credit Score"
            value={settings.contest_mode_correct_credit_score}
            onChange={(v) => handleChange('contest_mode_correct_credit_score', v)}
            required
          />
        </div>
      </Section>

      {/* Multi Match Settings */}
      <Section title="Multi Match Settings">
        <div className={wrapRowClass}>
          <ToggleSwitch
            label="Visible Mode"
            checked={!!settings.multi_match_mode}
            onChange={(v) => handleChange('multi_match_mode', v)}
          />
          <ToggleSwitch
            label="Fix Question in Level"
            checked={!!settings.multi_match_fix_level_question}
            onChange={(v) => handleChange('multi_match_fix_level_question', v)}
          />
          <InputField
            label="Total Question per Level"
            value={settings.multi_match_total_level_question}
            onChange={(v) => handleChange('multi_match_total_level_question', v)}
            required
          />
          <InputField
            label="Multi Match Duration (in seconds)"
            value={settings.multi_match_duration}
            onChange={(v) => handleChange('multi_match_duration', v)}
            required
          />
          <InputField
            label="Wrong Answer Deduct Score"
            value={settings.multi_match_wrong_answer_deduct_score}
            onChange={(v) => handleChange('multi_match_wrong_answer_deduct_score', v)}
            required
          />
          <InputField
            label="Correct Answer Credit Score"
            value={settings.multi_match_correct_answer_credit_score}
            onChange={(v) => handleChange('multi_match_correct_answer_credit_score', v)}
            required
          />
        </div>
      </Section>

      <div className="text-right mt-8">
        <button
          type="submit"
          className="px-8 py-3 text-lg font-semibold bg-[#00C09A] text-white rounded-lg shadow hover:bg-[#009b7a] transition"
        >
          Submit
        </button>
      </div>
    </form>
  );
};

export default QuizSettingsPage;
